import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { t, Trans } from '@lingui/macro';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@decisiv/ui-components';

import Helmet from '../../components/SEO/Helmet';
import PageTitle from '../../components/PageTitle/PageTitle';
import IntegrationHealthApi from '../../api/integration_health';
import authorizeOn from '../../utils/resourceAuthorizer';
import getCurrentEnv from '../../utils/getCurrentEnv';
import localCache from '../../utils/localCache';
import { STORAGE_KEYS } from '../../utils/storageKeys';
import HealthTable from './HealthTable';
import HealthCards from './HealthCards';
import HealthHeatmap from './HealthHeatmap';
import {
  Page,
  Chip,
  IconBtn,
  Select,
  Updated,
  Body,
  Tiles,
  Tile,
  Controls,
  Segmented,
  SegButton,
  Search,
  Legend,
  LegendCard,
  Pill,
  Foot,
  Empty,
  ViewSwitch,
  ViewButton,
} from './style';
import { timeAgo } from '../../utils/dates';

const FILTERS = ['all', 'unhealthy', 'degraded', 'healthy', 'idle'];
const STATUS_ORDER = { unhealthy: 0, degraded: 1, healthy: 2, idle: 3 };
const REFRESH_OPTIONS = [
  [0, 'Off'],
  [15000, '15s'],
  [30000, '30s'],
  [60000, '1m'],
  [300000, '5m'],
];
const VIEWS = ['table', 'cards', 'heatmap'];

export default function IntegrationHealthPage() {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [lastFetch, setLastFetch] = useState(Date.now());
  const [refreshMs, setRefreshMs] = useState(30000);
  const [view, setView] = useState(() => {
    const stored = localCache.get(STORAGE_KEYS.INTEGRATION_HEALTH_VIEW);
    return VIEWS.includes(stored) ? stored : 'table';
  });
  const [, setTick] = useState(0);
  const fetchSeq = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = fetchSeq.current + 1;
    fetchSeq.current = seq;
    setLoading(true);
    const result = await IntegrationHealthApi.fetchIntegrationHealth();
    if (seq !== fetchSeq.current) return;
    if (result.data) {
      setIntegrations(result.data);
      setLoadFailed(false);
    } else {
      setLoadFailed(true);
    }
    setLastFetch(Date.now());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!authorizeOn()) {
      navigate('/integrations');
      return undefined;
    }
    fetchData();
    const tick = setInterval(() => setTick((n) => n + 1), 10000);
    return () => clearInterval(tick);
  }, [fetchData]);

  // Polling pauses on a failed load so a rejected session doesn't retry every
  // tick; a successful manual refresh clears the flag and resumes it.
  useEffect(() => {
    if (!refreshMs || loadFailed) return undefined;
    const id = setInterval(fetchData, refreshMs);
    return () => clearInterval(id);
  }, [refreshMs, loadFailed, fetchData]);

  const counts = useMemo(() => {
    const base = {
      all: integrations.length,
      unhealthy: 0,
      degraded: 0,
      healthy: 0,
      idle: 0,
    };
    integrations.forEach((r) => {
      const s = (r.attributes || {}).status;
      if (base[s] !== undefined) base[s] += 1;
    });
    return base;
  }, [integrations]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const order = (r) => STATUS_ORDER[(r.attributes || {}).status] ?? 99;
    return integrations
      .filter((r) => filter === 'all' || (r.attributes || {}).status === filter)
      .filter(
        (r) =>
          !q || ((r.attributes || {}).name || '').toLowerCase().includes(q),
      )
      .sort((x, y) => order(x) - order(y));
  }, [integrations, filter, query]);

  const env = (
    getCurrentEnv(process.env.REACT_APP_BASE_URL || '') || 'qa'
  ).toUpperCase();
  const filterLabel = (f) =>
    ({
      all: t`All`,
      unhealthy: t`Unhealthy`,
      degraded: t`Degraded`,
      healthy: t`Healthy`,
      idle: t`No Activity`,
    }[f]);
  const viewLabel = (v) =>
    ({ table: t`Table`, cards: t`Cards`, heatmap: t`Map` }[v]);
  const selectView = (v) => {
    setView(v);
    localCache.set(STORAGE_KEYS.INTEGRATION_HEALTH_VIEW, v);
  };
  const tileClass = (tone) => {
    const alerting = tone === 'unhealthy' && counts.unhealthy > 0;
    return [tone === 'total' ? '' : tone, alerting ? 'alerting' : '']
      .filter(Boolean)
      .join(' ');
  };

  const emptyState = () => {
    let message = t`No active integrations to report on.`;
    if (query) message = t`Nothing matches your search.`;
    else if (filter !== 'all') message = t`Nothing in this tier right now.`;
    const clear = counts.unhealthy === 0 && counts.degraded === 0;
    return (
      <Empty>
        <div className="mark">{clear ? '✓' : '—'}</div>
        <h3>
          {clear && filter !== 'healthy' ? t`All clear` : t`Nothing to show`}
        </h3>
        <p>{message}</p>
      </Empty>
    );
  };

  const content = () => {
    if (loadFailed) {
      return (
        <Empty>
          <div
            className="mark"
            style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}
          >
            !
          </div>
          <h3>{t`Couldn’t load integration health`}</h3>
          <p>{t`Try refreshing. The scheduled Slack/Bugsnag alerts are unaffected.`}</p>
        </Empty>
      );
    }
    return (
      <>
        <Tiles>
          {[
            ['total', t`Active integrations`, counts.all],
            ['healthy', t`Healthy`, counts.healthy],
            ['degraded', t`Degraded`, counts.degraded],
            ['unhealthy', t`Unhealthy`, counts.unhealthy],
            ['idle', t`No Activity`, counts.idle],
          ].map(([tone, label, n]) => (
            <Tile
              key={tone}
              type="button"
              className={tileClass(tone)}
              aria-pressed={filter === (tone === 'total' ? 'all' : tone)}
              onClick={() => setFilter(tone === 'total' ? 'all' : tone)}
            >
              <div className="n">{n}</div>
              <div className="l">
                <span className="sw" />
                {label}
              </div>
            </Tile>
          ))}
        </Tiles>

        <Controls>
          <Segmented role="group" aria-label={t`Filter by status`}>
            {FILTERS.map((f) => (
              <SegButton
                key={f}
                type="button"
                className={f}
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
              >
                {filterLabel(f)} <span className="count">{counts[f]}</span>
              </SegButton>
            ))}
          </Segmented>
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <ViewSwitch role="group" aria-label={t`Choose view`}>
              {VIEWS.map((v) => (
                <ViewButton
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  onClick={() => selectView(v)}
                >
                  {viewLabel(v)}
                </ViewButton>
              ))}
            </ViewSwitch>
            <Search
              type="search"
              value={query}
              placeholder={t`Filter integrations…`}
              aria-label={t`Filter integrations`}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </Controls>

        {view === 'table' && (
          <HealthTable
            rows={rows}
            loading={loading && !integrations.length}
            emptyState={emptyState()}
          />
        )}
        {view === 'cards' && (
          <HealthCards
            rows={rows}
            loading={loading && !integrations.length}
            emptyState={emptyState()}
          />
        )}
        {view === 'heatmap' && (
          <HealthHeatmap
            rows={rows}
            loading={loading && !integrations.length}
            emptyState={emptyState()}
          />
        )}

        <Legend>
          <LegendCard>
            <div className="h">
              <Pill className="unhealthy">
                <span className="d" />
                {t`Unhealthy`}
              </Pill>
            </div>
            <div className="d">
              <Trans>
                ≥ 5 live executions, <b>zero succeeded</b>, ≥1 failed.{' '}
                <b>This pushes a Slack alert.</b>
              </Trans>
            </div>
          </LegendCard>
          <LegendCard>
            <div className="h">
              <Pill className="degraded">
                <span className="d" />
                {t`Degraded`}
              </Pill>
            </div>
            <div className="d">
              <Trans>
                Still succeeding, but an elevated rate of failures or
                circuit-breaker blocks. Early-warning only;{' '}
                <b>does not alert in v1</b>.
              </Trans>
            </div>
          </LegendCard>
          <LegendCard>
            <div className="h">
              <Pill className="healthy">
                <span className="d" />
                {t`Healthy`}
              </Pill>
            </div>
            <div className="d">
              <Trans>
                At least one success in the window (or low-volume). No action
                needed.
              </Trans>
            </div>
          </LegendCard>
          <LegendCard>
            <div className="h">
              <Pill className="idle">
                <span className="d" />
                {t`No Activity`}
              </Pill>
            </div>
            <div className="d">
              <Trans>
                No executions in the window and no success in the last 7 days —
                dormant, not erroring.
              </Trans>
            </div>
          </LegendCard>
        </Legend>

        <Foot>
          <span>
            <Trans>
              Live aggregate over <code>step_function_executions</code> (source
              = LIVE, trailing 6h) — the same detection that powers the
              scheduled Slack alert.
            </Trans>
          </span>
        </Foot>
      </>
    );
  };

  return (
    <Page>
      <Helmet
        title={t`Integration Health`}
        description={t`Health of every active integration`}
      />
      <PageTitle title={t`Integration Health`}>
        <Chip>
          <span className="dot" /> {t`Environment`}: {env}
        </Chip>
        <Chip>{t`Trailing window: 6h`}</Chip>
        <Updated>
          <span className="spin" /> {t`updated`} {timeAgo(lastFetch)}
        </Updated>
        <IconBtn type="button" onClick={fetchData} title={t`Refresh now`}>
          ↻ {t`Refresh`}
        </IconBtn>
        <Select
          aria-label={t`Auto-refresh interval`}
          value={refreshMs}
          onChange={(e) => setRefreshMs(Number(e.target.value))}
        >
          {REFRESH_OPTIONS.map(([ms, label]) => (
            <option key={ms} value={ms}>
              ↻ {ms === 0 ? t`Off` : label}
            </option>
          ))}
        </Select>
      </PageTitle>
      <Body>
        <div style={{ margin: '18px 0' }}>
          <Alert intent="information" title={t`Alerts also post to Slack`}>
            <Trans>
              Newly-unhealthy integrations post a digest to{' '}
              <code>#integration-alerts</code> — this page is for triage &amp;
              drill-down. Alerts fire once per incident (on transition), not
              every sweep.
            </Trans>
          </Alert>
        </div>
        {content()}
      </Body>
    </Page>
  );
}
